import { type Editor, useEditorState } from '@tiptap/react';
import { HeadingIcon } from 'lucide-react';
import { useMemo } from 'react';

import { HeadingButton, type HeadingDropdownMenuProps, type Level } from '@/components/ui/tiptap';
import { DropdownMenuWrapper } from '@/components/ui/tiptap/tiptap-ui-primitives';
import { useDropdownMenuState } from '@/components/ui/tiptap/tiptap-ui-primitives/dropdown-menu';
import { headingOptions } from '@/components/ui/tiptap/tiptap-ui/heading-dropdown-menu/HeadingButton';
import { useTiptapEditor } from '@/hooks/tiptap';
import { isNodeInSchema, shouldShow } from '@/lib/tiptap';

function isActive(editor: Editor | null, type: Level): boolean {
  if (!editor) return false;

  switch (type) {
    case 1:
      return editor.isActive('heading', { level: 1 });
    case 2:
      return editor.isActive('heading', { level: 2 });
    case 3:
      return editor.isActive('heading', { level: 3 });
    case 4:
      return editor.isActive('heading', { level: 4 });
    case 5:
      return editor.isActive('heading', { level: 5 });
    case 6:
      return editor.isActive('heading', { level: 6 });
    default:
      return false;
  }
}

function useIsAnyHeadingActive(editor: Editor | null): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => {
        return ctx.editor?.isActive('heading') ?? false;
      },
    }) ?? false
  );
}

function useIsHeadingButtonDisabled(editor: Editor | null, levels: Level[], userDisabled: boolean = false): boolean {
  const can: boolean =
    useEditorState({
      editor,
      selector: (ctx) => {
        return levels.some((level) => ctx.editor?.can().toggleNode('heading', 'paragraph', { level }));
      },
    }) ?? false;

  if (userDisabled) return true;
  if (!can) return true;
  return false;
}

export function HeadingDropdownMenu(headingDropdownMenuProps: HeadingDropdownMenuProps) {
  const { levels, editor: providedEditor, onOpenChange, ...props } = headingDropdownMenuProps;
  const editor: Editor | null = useTiptapEditor(providedEditor);
  const isAnyActive: boolean = useIsAnyHeadingActive(editor);
  const isDisabled: boolean = useIsHeadingButtonDisabled(editor, levels);
  const inSchema: boolean = isNodeInSchema('heading', editor);
  const { isOpen, filteredItems, handleOpenChange, activeIcon } = useDropdownMenuState<Level>(editor, levels, headingOptions, isActive, <HeadingIcon />);

  const show: boolean = useMemo(() => {
    return shouldShow(inSchema);
  }, [inSchema]);
  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenuWrapper
      isOpen={isOpen}
      onOpenChange={(open) => handleOpenChange(open, onOpenChange)}
      isActive={isAnyActive}
      ariaLabel="List options"
      buttonProps={props}
      activeIcon={activeIcon()}
      isDisabled={isDisabled}
    >
      <>
        {filteredItems.map((option) => (
          <HeadingButton key={`heading-${option.type}`} editor={editor} level={option.type} />
        ))}
      </>
    </DropdownMenuWrapper>
  );
}
